<template>
  <SkeletonDataTable
    :id="id"
    v-model:expanded-rows="expandedRows"
    :value="internalRateQuotes"
    :loading="loading"
    :number-of-rows="5"
    :row-class="getRowClass"
    class="waterfall-tender-table elevated"
    collapsed-row-icon="pi pi-chevron-down"
    expanded-row-icon="pi pi-chevron-up"
    tabindex="-1"
    @row-expand="onRowExpand($event.data)"
  >
    <template v-if="slots.empty" #empty>
      <slot name="empty" />
    </template>

    <Column
      :header="t('carriers.carriers')"
      class="carrier-column"
      field="carrier.scac"
    >
      <template #body="{ data }: { data: WaterfallTenderRateQuote }">
        <RateQuoteCarrier
          :rate-quote="data.rateQuote"
          with-context-chat
          class="carrier-column-content"
        />

        <RateQuoteWarnings
          :rate-quote="data.rateQuote"
          @click="onChipClicked(data)"
        />
      </template>
    </Column>

    <Column :header="t('carriers.modes')" class="w-1" field="rateQuote.mode">
      <template #body="{ data }: { data: WaterfallTenderRateQuote }">
        {{
          data.rateQuote.mode ? translateEnum(data.rateQuote.mode, 'Mode') : '-'
        }}
      </template>
    </Column>

    <Column :header="t('rateShop.service')" class="w-1">
      <template #body="{ data }: { data: WaterfallTenderRateQuote }">
        {{
          data.rateQuote.serviceType
            ? titleCase(data.rateQuote.serviceType)
            : '-'
        }}
        <div class="text-gray-darker sub-heading">
          {{ renderServiceInfo(data.rateQuote) }}
        </div>
      </template>
    </Column>

    <Column :header="t('rateShop.transit')" class="w-1">
      <template #body="{ data }: { data: WaterfallTenderRateQuote }">
        {{
          data.rateQuote.transitTime
            ? t('rateShop.countDays', { count: data.rateQuote.transitTime })
            : '-'
        }}
      </template>
    </Column>

    <Column :header="t('rateShop.rateSource')" class="source-column">
      <template #body="{ data }: { data: WaterfallTenderRateQuote }">
        <RateShopTableSource
          :rate-quote="data.rateQuote"
          :waterfall-tender-rate-quote="data"
          :disabled="loading"
          can-edit-rate
          @edit-quote="emit('edit-quote', $event)"
          @edit-spot-quote="emit('edit-spot-quote', $event)"
        />
      </template>
    </Column>

    <Column
      :header="t('common.totalParam', { param: t('rateShop.price') })"
      class="price-column text-right"
    >
      <template #body="{ data }: { data: WaterfallTenderRateQuote }">
        <div
          class="price-column-content flex justify-content-end align-items-center"
        >
          <span :class="priceClass">
            {{
              renderMoney(data.rateQuote.totalPrice, {
                currencyCode: data.rateQuote.currencyCode,
              })
            }}
          </span>
        </div>
      </template>
    </Column>

    <Column
      :header="t('common.paramStatuses', { param: t('shipments.tender') })"
      class="pl-6 w-full"
    >
      <template #body="{ data }: { data: WaterfallTenderRateQuote }">
        <div class="flex flex-column align-items-start">
          <WaterfallTenderRateQuoteStatusChip
            :status="data.status"
            :tender-response-code="
              data.tenderRequest?.tenderResponse?.responseCode
            "
          />
          <TimeAgo
            v-if="getStatusDate(data)"
            v-slot="{ timeAgo }"
            :max="60 * 60 * 1000"
            :time="getStatusDate(data)?.toJSDate() as Date"
            :full-date-formatter="(date: Date) => d(date, 'numericDateTime')"
          >
            <small class="mt-1 ml-2">{{ timeAgo }}</small>
          </TimeAgo>
        </div>
      </template>
    </Column>

    <Column :header-style="{ width: '3rem', 'min-width': '3rem' }" expander />

    <template #expansion="{ data }: { data: WaterfallTenderRateQuote }">
      <RateQuoteExpandedDetails
        :active-tab-key="rateQuoteToTabKeyMap.get(data.id!)"
        :rate-quote="data.rateQuote"
      />
    </template>
  </SkeletonDataTable>
</template>

<script lang="ts" setup>
import { UseTimeAgo as TimeAgo } from '@vueuse/components';
import { DateTime } from 'luxon';
import type { ClassValue, PropType, Ref } from 'vue';
import { nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useExpandedRows, useUid } from '@rygen/rygen-ui/composables';
import { renderMoney, titleCase, translateEnum } from '@rygen/rygen-ui/i18n';
import { notNull } from '@rygen/rygen-ui/util';

import {
  RateQuote,
  WaterfallTenderRateQuote,
  WaterfallTenderRateQuoteStatus,
} from '@/model';

import RateQuoteExpandedDetails, {
  TabKey,
} from '@/components/rateShop/expandedDetails/RateQuoteExpandedDetails.vue';
import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RateQuoteWarnings from '@/components/rateShop/RateQuoteWarnings.vue';
import RateShopTableSource from '@/components/rateShop/RateShopTableSource.vue';
import WaterfallTenderRateQuoteStatusChip from '@/components/rateShop/WaterfallTenderRateQuoteStatusChip.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('waterfall-tender-table').uid,
  },
  rateQuotes: {
    type: Array as PropType<WaterfallTenderRateQuote[]>,
    required: true,
  },
  priceClass: {
    type: [String, Object] as PropType<ClassValue>,
    default: () => 'text-success font-medium',
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuotes, priceClass, loading } = toRefs(props);
const emit = defineEmits<{
  (event: 'edit-spot-quote', spotQuote: RateQuote): void;
  (event: 'edit-quote', rateQuote: RateQuote): void;
}>();

const slots = defineSlots<{
  empty?: () => any;
}>();

const { d, t } = useI18n();

const internalRateQuotes: Ref<WaterfallTenderRateQuote[]> = ref([]);
const { expandedRows, getRowClass } = useExpandedRows(internalRateQuotes, {
  expandIfSingleItem: false,
});

const entries: [number, TabKey | undefined][] = internalRateQuotes.value
  .map((r) => r.id)
  .filter(notNull)
  .map((rateQuoteId) => [rateQuoteId, undefined]);
const rateQuoteToTabKeyMap: Ref<Map<number, TabKey | undefined>> = ref(
  new Map(entries),
);

const onRowExpand = (rateQuote: WaterfallTenderRateQuote) => {
  if (rateQuote.id) {
    rateQuoteToTabKeyMap.value.set(rateQuote.id, undefined);
  }
};

const onChipClicked = (rateQuote: WaterfallTenderRateQuote) => {
  const isRowExpanded = expandedRows.value.includes(rateQuote);

  if (!isRowExpanded) {
    expandedRows.value.push(rateQuote);
  }

  nextTick(() =>
    rateQuoteToTabKeyMap.value.set(rateQuote.id!, 'OTHER_DETAILS'),
  );
};

const renderServiceInfo = (rateQuote: RateQuote) => {
  return [rateQuote.serviceTypeDescription, rateQuote.service]
    .filter((x) => x)
    .map((x) => titleCase(x))
    .join(' • ');
};

const getStatusDate = (
  rateQuote: WaterfallTenderRateQuote,
): DateTime | undefined => {
  switch (rateQuote.status) {
    case WaterfallTenderRateQuoteStatus.TENDERED:
      return rateQuote.tenderRequest?.createdDate;
    case WaterfallTenderRateQuoteStatus.COMPLETED:
      return rateQuote.tenderRequest?.tenderResponse?.responseDate;
    case WaterfallTenderRateQuoteStatus.EXPIRED:
      return rateQuote.tenderRequest?.expirationDate;
    default:
      return undefined;
  }
};

watch(
  rateQuotes,
  (newVal: WaterfallTenderRateQuote[]) => {
    internalRateQuotes.value = newVal.map(
      (x) => new WaterfallTenderRateQuote(x),
    );
  },
  { immediate: true, deep: true },
);
</script>

<style>
.waterfall-tender-table {
  .p-datatable.p-component {
    .datatable-no-header {
      .p-datatable-thead {
        display: none;
      }

      tr:last-of-type td {
        border-bottom: none;
      }
    }

    .p-datatable-table {
      .p-datatable-thead {
        .carrier-column {
          max-width: 25%;
          width: 25%;
        }

        .source-column {
          /** left/right padding = normal th padding + button padding */
          padding-inline: calc(var(--sp-2) + 0.875rem);
        }
      }

      .datatable-row {
        td {
          .rate-response-table-source {
            /** match button padding so 'Contract' lines up with Spot Quote button */
            padding: 0.4375rem 0.875rem;
          }
        }

        .sub-heading {
          font-size: var(--sp-3);
        }

        .carrier-column {
          max-width: 25%;
          width: 25%;

          .carrier-column-content {
            gap: var(--sp-2);
          }

          .rate-quote-warnings {
            margin-top: var(--sp-3);
          }
        }

        .source-column {
          /*
            width of the column when the source is a button so all rows line up
          */
          width: 116.817px;
        }

        .price-column {
          .price-column-content {
            gap: var(--sp-1);
          }
        }
      }

      .p-datatable-row-expansion {
        & > td {
          padding: var(--sp-3);
        }
      }
    }
  }
}
</style>
